/**
 * 通用弹窗（支持一层嵌套）
 *
 * 纪律：
 * - 维护一个弹窗栈而不是单例：技能树里点开知识图鉴、答题后看讲解都会用到；
 * - `role="dialog"` + `aria-modal`，Esc 关闭最上层；
 * - 关闭即从 DOM 移除，不留残影。
 */

import { esc } from "./dom.js";

export interface ModalAction {
  label: string;
  value: string;
  kind?: "primary" | "ghost";
  disabled?: boolean;
}

export interface ModalOptions {
  eyebrow?: string;
  title: string;
  /** 已经转义过的 HTML */
  bodyHtml: string;
  actions?: ModalAction[];
  /** 点背景或按 Esc 是否可关闭（答题时不允许误关） */
  dismissible?: boolean;
  onAction?(value: string): void;
  onClose?(): void;
}

export interface ModalHandle {
  element: HTMLElement;
  /** 重新渲染主体（技能树升级后会用到） */
  setBody(bodyHtml: string, options?: { eyebrow?: string; title?: string }): void;
  /** 重新渲染动作按钮 */
  setActions(actions: ModalAction[]): void;
  close(): void;
}

interface Entry {
  element: HTMLElement;
  onClose?: () => void;
}

const stack: Entry[] = [];

function attachKeyHandler(): void {
  document.addEventListener("keydown", handleKeyDown);
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  const top = stack[stack.length - 1];
  if (top) closeEntry(top);
}

function closeEntry(entry: Entry): void {
  const index = stack.indexOf(entry);
  if (index === -1) return;
  stack.splice(index, 1);
  entry.element.remove();
  if (stack.length === 0) document.removeEventListener("keydown", handleKeyDown);
  entry.onClose?.();
}

export function closeTopModal(): void {
  const top = stack[stack.length - 1];
  if (top) closeEntry(top);
}

export function closeAllModals(): void {
  while (stack.length > 0) {
    const top = stack[stack.length - 1]!;
    closeEntry(top);
  }
}

export function openModal(options: ModalOptions): ModalHandle {
  const root = document.getElementById("modal-root");
  if (!root) throw new Error("缺少 #modal-root");

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.setAttribute("role", "dialog");
  backdrop.setAttribute("aria-modal", "true");
  backdrop.setAttribute("aria-label", options.title);

  const panel = document.createElement("div");
  panel.className = "modal paper";
  backdrop.append(panel);
  root.append(backdrop);

  const header = { eyebrow: options.eyebrow, title: options.title };
  let actions: ModalAction[] = options.actions ?? [];

  // 结构只建一次：头 / 体 / 动作区各自独立更新，
  // 这样"答题后只换按钮"不会把已经渲染好的讲解内容与监听一起冲掉。
  panel.innerHTML = `
    <div class="modal-head"><div data-header></div></div>
    <div class="modal-body" data-body></div>
    <div class="modal-actions" data-actions></div>`;

  const headerHost = panel.querySelector<HTMLElement>("[data-header]");
  const bodyHost = panel.querySelector<HTMLElement>("[data-body]");
  const actionsHost = panel.querySelector<HTMLElement>("[data-actions]");

  const renderHeader = (): void => {
    if (!headerHost) return;
    headerHost.innerHTML = `
      ${header.eyebrow ? `<p class="modal-eyebrow">${esc(header.eyebrow)}</p>` : ""}
      <h2 class="modal-title">${esc(header.title)}</h2>`;
  };

  const renderActions = (): void => {
    if (!actionsHost) return;
    actionsHost.innerHTML = actions
      .map(
        (action) =>
          `<button class="btn${action.kind === "ghost" ? " btn--ghost" : ""}" type="button"
             data-action="${esc(action.value)}" ${action.disabled ? "disabled" : ""}>${esc(
               action.label,
             )}</button>`,
      )
      .join("");
    actionsHost.hidden = actions.length === 0;
  };

  const renderBody = (bodyHtml: string): void => {
    if (bodyHost) bodyHost.innerHTML = bodyHtml;
  };

  renderHeader();
  renderBody(options.bodyHtml);
  renderActions();

  panel.onclick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLElement>("[data-action]");
    if (!button || button.hasAttribute("disabled")) return;
    const value = button.dataset["action"];
    if (value) options.onAction?.(value);
  };

  const entry: Entry = { element: backdrop, ...(options.onClose ? { onClose: options.onClose } : {}) };
  stack.push(entry);

  if (options.dismissible !== false) {
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) closeEntry(entry);
    });
  }
  if (stack.length === 1) attachKeyHandler();

  return {
    element: backdrop,
    setBody(bodyHtml, overrides) {
      if (overrides?.title) header.title = overrides.title;
      if (overrides?.eyebrow !== undefined) header.eyebrow = overrides.eyebrow;
      renderHeader();
      renderBody(bodyHtml);
    },
    setActions(next) {
      actions = next;
      renderActions();
    },
    close() {
      closeEntry(entry);
    },
  };
}

/** 单按钮信息弹窗 */
export function infoModal(title: string, bodyHtml: string, confirmLabel = "知道了"): ModalHandle {
  return openModal({
    title,
    bodyHtml,
    actions: [{ label: confirmLabel, value: "ok" }],
    onAction: () => closeTopModal(),
  });
}
